import type { ApprovalOddsItem, ApprovalOddsMap } from '@cc-shopping/approval-odds/odds';
import {
  isSpinwheelBypassAllowedForEnv,
  isSpinwheelBypassOddsId,
  parseSpinwheelBypassOddsTier,
  SPINWHEEL_BYPASS_ODDS_QUERY_PARAM,
} from '@cc-shopping/approval-odds/spinwheelBypass';

/**
 * This utility exists mainly to support the Spinwheel bypass mode. Using a static
 * mock response is more brittle than mapping synthetic tiers onto any offers response.
 * It is mapped deterministically by the offer id hash.
 *
 * Direct product-id hit wins. Otherwise, in Spinwheel bypass mode, maps a synthetic
 * `spinwheel-bypass-*` fixture tier onto the card (`?bypass_sw_odds=<tier>` forces one).
 * The fallback is gated to the same non-prod env allowlist as the BFF bypass.
 *
 * The Spinwheel bypass is only available in non-prod environments.
 */
export function resolveActivationOdds(
  approvalLikelihoods: ApprovalOddsMap,
  productId: string,
): ApprovalOddsItem | undefined {
  const direct = approvalLikelihoods[productId];
  if (direct) {
    return direct;
  }

  if (
    !isSpinwheelBypassAllowedForEnv(
      // biome-ignore lint/style/noProcessEnv: bypass must read the raw env, not the fail-open schema fallback
      process.env.NEXT_PUBLIC_DEPLOYABLE_ENV,
    )
  ) {
    return undefined;
  }

  const fixtures = Object.entries(approvalLikelihoods)
    .filter(([id]) => isSpinwheelBypassOddsId(id))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, item]) => item);

  if (fixtures.length === 0) {
    return undefined;
  }

  const forcedTier = readForcedTier();
  const fixture =
    fixtures.find(item => item.approvalLikelihood === forcedTier) ??
    fixtures[hashString(productId) % fixtures.length];

  return fixture ? { ...fixture, id: productId } : undefined;
}

function readForcedTier() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return parseSpinwheelBypassOddsTier(
    new URLSearchParams(window.location.search).get(SPINWHEEL_BYPASS_ODDS_QUERY_PARAM),
  );
}

// Basic offer id hash for deterministic odds when not forced
function hashString(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}
